import { Button } from "@/components/ui-library/button/Button"
import { useOnClickOutside } from "@/hooks/useOnClickOutside"
import { usePreventScroll } from "@/hooks/usePreventScroll"
import CloseIcon from "@/layout/icons/CloseIcon"
import { cn } from "@/utils/utils"
import { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

export type ModalCloseButtonPosition = "left" | "right"

type ModalProps = {
  children: React.ReactNode
  /** Sizing and corners for the panel wrapper, so callers can vary them per breakpoint in CSS. */
  panelClassName?: string
  /** The content area below the close button. */
  className?: string
  /** Visibility of the close-button band — a caller that only wants it on some viewports passes
   *  responsive classes here rather than toggling `useCloseButton` from a media-query hook. */
  closeButtonClassName?: string
  useCloseButton?: boolean
  onClose: () => void
  closeButtonPosition?: ModalCloseButtonPosition
  marginY?: string
  marginX?: string
}

export const Modal = ({
  children,
  panelClassName = "h-5/6 w-5/6",
  className,
  closeButtonClassName,
  useCloseButton = true,
  onClose,
  closeButtonPosition = "right",
  marginY = ".5rem",
  marginX = "1.0rem"
}: ModalProps) => {
  const onCloseCallback = useCallback(() => onClose(), [onClose])
  const modalRef = useRef<HTMLDivElement>(null)
  useOnClickOutside(modalRef, onCloseCallback)
  usePreventScroll(true)

  // Portalled to <body> so the overlay escapes its caller's stacking context. Triggers sit inside
  // positioned wrappers — VaccinationFloatingCta is `fixed z-50`, clinic cards animate with a
  // transform — and each of those creates a stacking context that traps this overlay beneath the
  // site header, no matter how high its z-index goes. A portal is the only fix that scales; the
  // alternative is raising every trigger above the header and hoping the next one remembers.
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  if (!mounted) return null

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50">
      <div className={cn("flex flex-col", panelClassName)}>
        {useCloseButton && (
          <div
            className={cn(
              "flex h-14 shrink-0 items-center rounded-t-lg bg-surface",
              closeButtonPosition === "right" ? "justify-end" : "justify-start",
              closeButtonClassName
            )}
          >
            <Button
              onClick={onCloseCallback}
              className="h-10"
              style={{
                marginLeft: marginX,
                marginRight: marginX,
                marginTop: marginY,
                marginBottom: marginY
              }}
            >
              <CloseIcon />
            </Button>
          </div>
        )}
        <div ref={modalRef} className={cn("min-h-0 grow bg-white p-6 shadow-lg", className)}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  )
}

export default Modal
